/**
 * Cómo se rotula un archivo adjunto en una lista.
 *
 * Vive aparte de la pantalla porque tiene bordes que conviene fijar con pruebas —
 * un nombre sin extensión, un punto que no encabeza una, un nombre que ya cabe— y
 * porque la pantalla no debería tener que saber ninguno de ellos.
 */

/** El último segmento de una ruta de Storage: la ruta entera es ruido en una lista. */
export const fileNameOf = (path: string): string => path.split('/').pop() ?? path;

/**
 * Cuántos caracteres de nombre caben antes de que el botón empiece a costarle
 * ancho a la línea que dice qué pasó.
 *
 * Medido contra la tarjeta real de la bitácora —745px—: con este tope la fila
 * mide lo mismo que si el archivo se llamara `constancia.pdf`, que es la
 * referencia de «no estorba».
 */
export const MAX_FILE_LABEL = 28;

/** La extensión más larga que se reconoce como tal, contando el punto. */
const MAX_EXTENSION = 6;

/**
 * El nombre recortado, conservando la extensión.
 *
 * Los documentos que sube el portal llevan un UUID pegado —
 * `ComprobanteDePago-044695b4-6f39-4fff-9ecf-8123ad8c293e.pdf`, 58 caracteres— y
 * ese rótulo vive en una pista `auto` de la rejilla del evento. Una pista `auto`
 * se dimensiona al contenido y el botón lleva `white-space: nowrap`, así que no
 * cedía un píxel: se llevaba 456 de 700, la columna del texto se quedaba en 158 y
 * la descripción del movimiento se partía en una palabra por renglón. La fila
 * pasaba de 62px de alto a 118.
 *
 * **Se recorta aquí y no con `text-overflow`** porque lo que ese corte se llevaría
 * es la extensión, que es lo primero que alguien quiere saber de un adjunto. El
 * `…` va antes de ella, no después del final.
 *
 * El nombre entero sigue existiendo: la pantalla lo pone en el `title` y en el
 * `aria-label`, que es donde se lee sin costarle ancho a nadie.
 */
export const shortFileName = (name: string): string => {
  if (name.length <= MAX_FILE_LABEL) return name;

  const dot = name.lastIndexOf('.');
  /*
   * `dot > 0` y no `dot >= 0`: un punto en la posición cero es un archivo oculto
   * —`.gitignore`— y no una extensión. Tratarlo como tal dejaría el nombre entero
   * como «extensión» y el tallo vacío.
   */
  const extension = dot > 0 && name.length - dot <= MAX_EXTENSION ? name.slice(dot) : '';
  const stem = extension.length > 0 ? name.slice(0, dot) : name;

  // Al menos un carácter de nombre, aunque la extensión se coma el presupuesto.
  const keep = Math.max(1, MAX_FILE_LABEL - extension.length - 1);

  return `${stem.slice(0, keep)}…${extension}`;
};
